import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, type Node, type LatestMetric, humanBytes, timeAgo } from '../lib/api'
import { PageHeader, Button, Table, Modal, Field, Input, Checkbox, ErrorBox } from '../components/ui'
import { IconPlus, IconCircleDot } from '../components/icons'
import { UsageBar } from '../components/UsageBar'
import { ActiveTaskBadge } from '../components/ActiveTaskBadge'

const STATUS_RU: Record<string, string> = {
  online: 'онлайн', registered: 'подключена', installing: 'установка',
  pending: 'ожидание', failed: 'ошибка', offline: 'офлайн',
}
const STATUS_TONE: Record<string, 'green'|'blue'|'yellow'|'red'|'gray'> = {
  online: 'green', registered: 'blue', installing: 'blue',
  pending: 'gray', failed: 'red', offline: 'gray',
}

export function NodesPage() {
  const nav = useNavigate()
  const [addOpen, setAddOpen] = useState(false)

  const { data = [] } = useQuery({
    queryKey: ['nodes'],
    queryFn: async () => (await api.get<Node[]>('/nodes')).data,
    refetchInterval: 5000,
  })
  const { data: metrics = {} } = useQuery({
    queryKey: ['nodes-metrics-latest'],
    queryFn: async () => (await api.get<Record<string, LatestMetric>>('/nodes-metrics-latest')).data,
    refetchInterval: 5000,
  })

  return (
    <>
      <PageHeader
        title="Ноды"
        subtitle="Серверы, где размещаются сайты, базы и почта. Добавьте ноду — мастер сам установит агент по SSH."
        actions={
          <Button onClick={() => setAddOpen(true)}>
            <IconPlus /> Добавить ноду
          </Button>
        }
      />
      <Table<Node>
        empty="Пока нет ни одной ноды. Нажмите «Добавить ноду»."
        rows={data}
        onRowClick={(n) => nav(`/nodes/${n.id}`)}
        cols={[
          { key: 'name', label: 'Имя', mobileLabel: 'Имя',
            render: (n) => <Link to={`/nodes/${n.id}`} className="font-medium text-neutral-900 hover:underline">{n.name}</Link> },
          { key: 'host', label: 'Хост', render: (n) => <span className="text-neutral-600 text-[13px]">{n.hostname}</span> },
          { key: 'ip', label: 'IP', render: (n) => <span className="font-mono text-[12px] text-neutral-600">{n.ip_address}</span> },
          { key: 'os', label: 'ОС', render: (n) => <span className="text-neutral-600 text-[12px]">{n.os_name || '—'}</span> },
          { key: 'res', label: 'Загрузка', w: '20%',
            render: (n) => {
              const m = metrics[n.id]
              if (!m) return (
                <span className="text-[11px] text-neutral-400">
                  {n.cpu_count ? `${n.cpu_count} vCPU · ${humanBytes(n.mem_bytes)}` : '—'}
                </span>
              )
              return (
                <div className="space-y-1.5">
                  <UsageBar label="CPU" percent={m.cpu_percent} />
                  <UsageBar label="RAM" used={m.mem_used} total={m.mem_total} />
                  <UsageBar label="Диск" used={m.disk_used} total={m.disk_total} />
                </div>
              )
            }},
          { key: 'st', label: 'Статус',
            render: (n) => (
              <div className="flex flex-col gap-1">
                <span className="inline-flex items-center gap-1.5 text-[13px] text-neutral-700">
                  <IconCircleDot tone={STATUS_TONE[n.status] || 'gray'} />
                  {STATUS_RU[n.status] || n.status}
                </span>
                <ActiveTaskBadge nodeId={n.id} />
              </div>
            )},
          { key: 'hb', label: 'Пинг', render: (n) => <span className="text-[12px] text-neutral-500">{timeAgo(n.last_heartbeat_at)}</span> },
        ]}
      />
      {addOpen && <AddNodeModal onClose={() => setAddOpen(false)} />}
    </>
  )
}

function AddNodeModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [f, setF] = useState({ name: '', hostname: '', ip_address: '', ssh_user: 'root', ssh_port: 22, ssh_password: '', auto_bootstrap: true })
  const [err, setErr] = useState('')
  const create = useMutation({
    mutationFn: async () => (await api.post<Node>('/nodes', f)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['nodes'] }); onClose() },
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })
  return (
    <Modal open onClose={onClose} title="Добавить ноду">
      <form onSubmit={(e) => { e.preventDefault(); create.mutate() }} className="space-y-4">
        <Field label="Имя" hint="Короткое имя ноды, например h2">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="h2" autoFocus required />
        </Field>
        <Field label="Хост (домен)">
          <Input value={f.hostname} onChange={(e) => setF({ ...f, hostname: e.target.value })} placeholder="h2.beebro.cloud" required />
        </Field>
        <Field label="IP-адрес">
          <Input value={f.ip_address} onChange={(e) => setF({ ...f, ip_address: e.target.value })} placeholder="46.8.189.65" required />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="SSH-пользователь"><Input value={f.ssh_user} onChange={(e) => setF({ ...f, ssh_user: e.target.value })} /></Field>
          <Field label="Порт"><Input type="number" value={f.ssh_port} onChange={(e) => setF({ ...f, ssh_port: +e.target.value })} /></Field>
          <Field label="Пароль"><Input type="password" value={f.ssh_password} onChange={(e) => setF({ ...f, ssh_password: e.target.value })} /></Field>
        </div>
        <Checkbox label="Автоматически установить агент по SSH"
                  checked={f.auto_bootstrap} onChange={(e) => setF({ ...f, auto_bootstrap: e.target.checked })} />
        {err && <ErrorBox>{err}</ErrorBox>}
        <div className="flex justify-end gap-2 pt-2 border-t border-neutral-100 -mx-5 px-5 pt-4">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Добавляем…' : 'Добавить'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

